import { SnapUtils, SnapSorterBuilder, ComponentIds, Vector, ConnectionUtils, HierarchyUtils } from '@inter-ikea-core/core';
import { ProductType } from '@inter-ikea-storageone/range';
import { BACK_WALL, FRONT_WALL, LEFT_WALL, RIGHT_WALL } from '@inter-ikea-storagetwo/range/dist/constants/connectorKeys';
import { SIZE_TO_HOST_CONNECTOR_MAP, UPRIGHT_TO_MOUNTING_RAIL, UPRIGHT_TO_MOUNTING_RAIL_LEFT } from '@inter-ikea-storagetwo/boaxel/dist/constants/connectorKeys';
import { getConnectionByKey } from '../utils/connectorUtils';
import UprightComponent from '@inter-ikea-storagetwo/boaxel/dist/components/UprightComponent';
const WALL_PRIORITY = [BACK_WALL, LEFT_WALL, RIGHT_WALL, FRONT_WALL];
/**
 * Adds a section to the wall. If any snap is invalid, the entities are disposed.
 * @param core Core API
 * @param mountingRail Mounting rail entity to be placed
 * @param uprightsComponent Component holding data for uprights and width
 */
const addToWall = (core, mountingRail, uprightsComponent) => {
    //When coming from addToSection there's some issue with rollback.
    //Disconnect from previous attempt.
    const railHasParent = HierarchyUtils.parent(mountingRail);
    if (railHasParent)
        ConnectionUtils.disconnect(mountingRail);
    const viewer = core.getServices().coreModules.viewer();
    const { uprightEntities: { uprightLeft, uprightRight }, width, position } = uprightsComponent;
    const roomMinX = core.kvadratRoom?.getRoomSize()?.min.X;
    const defaultPosition = getDefaultPosition(mountingRail, roomMinX);
    const raycastPosition = viewer?.raycast(position?.x, position?.y);
    const uprightHeight = uprightLeft.getComponent(ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID)?.size.height;
    const adjustedPosition = raycastPosition
        ? new Vector(raycastPosition.X, Math.max(raycastPosition.Y, uprightHeight ?? 0), raycastPosition.Z)
        : defaultPosition;
    const snapResults = getUprightsSnapResults(mountingRail, uprightLeft, uprightRight, core, width);
    if (!snapResults.every(snap => snap.valid))
        return;
    snapResults.forEach(snap => snap.snap?.performMoveAndConnect());
    const placement = core.placementService.snapToPosition(mountingRail, adjustedPosition, false);
    if (!placement.valid) {
        mountingRail.dispose();
        uprightLeft.dispose();
        uprightRight.dispose();
    }
};
/**
 * Adds a section to an existing sections right upright.
 * If the right upright snap fails the snapped mounting rail is roll-backed
 * and the section will be placed on the wall instead.
 * @param core Core api
 * @param mountingRail Mounting rail entity to be placed
 * @param mountingRailSnapResult Snap result for mounting rail
 * @param uprightsComponent Component holding data for uprights and width
 */
export const addToSection = (core, mountingRail, mountingRailSnapResult, uprightsComponent) => {
    const { uprightEntities: { uprightLeft, uprightRight }, width } = uprightsComponent;
    mountingRailSnapResult.snap?.performMoveAndConnect();
    const rightUprightSnapCandidate = getUprightSnapCandidate(uprightRight, mountingRail, UPRIGHT_TO_MOUNTING_RAIL, SIZE_TO_HOST_CONNECTOR_MAP[width]);
    const uprightSnapResult = core
        .getServices()
        .validations.validateSnapCandidate(rightUprightSnapCandidate);
    if (uprightSnapResult.valid) {
        uprightLeft.dispose();
        uprightSnapResult.snap?.performMoveAndConnect();
    }
    else {
        mountingRailSnapResult.snap?.rollbackMove();
        addToWall(core, mountingRail, uprightsComponent);
    }
};
/**
 * Adds a section to an existing section if possible, otherwise adds it to the wall.
 * @param core Core API
 * @param mountingRail Mounting rail to be placed
 * @param uprightsComponent Component holding data for uprights and width
 */
export const addSection = (core, mountingRail, uprightsComponent) => {
    const snapResult = getMountingRailSnapResult(core, mountingRail);
    const { uprightEntities, position } = uprightsComponent;
    const hostCandidate = snapResult.snap?.host;
    const grandParentRail = hostCandidate ? HierarchyUtils.parent(hostCandidate) : undefined;
    const parentHeight = grandParentRail?.getComponent(ComponentIds.WORLD_TRANSFORM_COMPONENT_ID)?.transform.position.Y;
    const uprightHeight = uprightEntities.uprightRight.getComponent(ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID)?.size.height;
    if (parentHeight == null || uprightHeight == null) {
        //If height data not present, move away from found section and add to wall.
        addToWall(core, mountingRail, uprightsComponent);
        return;
    }
    //Handle if section is too low for placement, since core acting up.
    const hasEnoughHeight = parentHeight >= uprightHeight;
    if (!position && hasEnoughHeight && snapResult.valid && canAttachToSection(mountingRail, core)) {
        addToSection(core, mountingRail, snapResult, uprightsComponent);
    }
    else {
        addToWall(core, mountingRail, uprightsComponent);
    }
};
/**
 * Get the first valid snap candidate on uprights in the scene for a mounting rail
 * @param core Core api
 * @param mountingRail Mounting rail to validate upright snap candidates for
 * @returns null if no valid snaps are found. Otherwise a ValidSnapActionCandidateOrErrors object
 */
export const getMountingRailSnapResult = (core, mountingRail) => {
    const uprights = core.entities.getByProductType(ProductType.BOAXEL_UPRIGHT);
    const candidates = SnapUtils.getMatchingSnapsOnMultipleHosts(mountingRail, uprights);
    return core.getServices().validations.getFirstValidSnap(candidates);
};
/**
 * Retrieves all possible snap action candidates in the scene and sorts them
 * so sections are prioritized over wall candidates
 * @param mountingRail Mounting rail entity to add
 * @param core Core
 * @returns Sorted snap action candidates
 */
export const getMountingRailSnapCandidate = (core, mountingRail) => {
    const candidates = SnapUtils.getMatchingSnapsInScene(mountingRail, core.getServices().sceneManager);
    const sorter = new SnapSorterBuilder()
        .addCustomCriterion(preferSectionsOverWalls)
        .addRecommendedDistanceFromFloorCriterion()
        .build();
    return sorter.sortCandidates(candidates);
};
/**
 * Sort criteria for sorting snap action candidates for mounting rails
 * Will prioritize sections over walls if they are present, otherwise
 * will sort walls according to WALL_PRIORITY
 */
const preferSectionsOverWalls = {
    bestCandidate: (a, b) => {
        if (a.priority > b.priority)
            return a;
        else if (a.priority < b.priority)
            return b;
        else if (isComparingWallCandidates(a, b))
            return sortWallCandidates(a, b);
        else
            return a;
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    bestResult: (_a, _b) => null // bestResult function is required on SortCriteria but not used.
};
const isComparingWallCandidates = (a, b) => a.host.hasComponent(ComponentIds.ROOM_PART_COMPONENT_ID) &&
    b.host.hasComponent(ComponentIds.ROOM_PART_COMPONENT_ID);
/**
 * Sorts wall snap candidates in order: back > left > right > front
 * @param a Snap action candidate to evaluate
 * @param b Snap action candidate to evaluate
 * @returns Sorted wall candidates
 */
const sortWallCandidates = (a, b) => {
    const aCandidatePriority = WALL_PRIORITY.indexOf(a.hostConnector.key);
    const bCandidatePriority = WALL_PRIORITY.indexOf(b.hostConnector.key);
    if (aCandidatePriority < bCandidatePriority)
        return a;
    if (aCandidatePriority > bCandidatePriority)
        return b;
    return a;
};
/**
 * Gets a snap candidate for a specific connection based on provided host- & guest-key.
 * Throws error if no connector exists or snap candidate couldn't be created.
 * @param guestEntity Upright entity to attach to mounting rail
 * @param hostEntity Mounting rail entity
 * @param guestKey Upright connector key to retrieve connector
 * @param hostKey Mounting rail connector key to retrieve connector
 * @returns SnapActionCandidate
 */
export const getUprightSnapCandidate = (guestEntity, hostEntity, guestKey, hostKey) => {
    const guestConnector = getConnectionByKey(guestEntity, guestKey);
    const hostConnector = getConnectionByKey(hostEntity, hostKey);
    const snapCandidate = SnapUtils.getSnap(guestEntity, guestConnector, hostEntity, hostConnector);
    if (!snapCandidate)
        throw new Error(`Couldn't create candidate with ${guestEntity} and ${hostEntity}`);
    return snapCandidate;
};
/**
 * Check if any upright snap action candidates exists. Does not validate the candidate.
 * @param mountingRail Mounting rail to add
 * @param core Core
 * @returns true if a upright snap action candidate exists, otherwise false.
 */
const canAttachToSection = (mountingRail, core) => {
    const candidates = getMountingRailSnapCandidate(core, mountingRail);
    return candidates.some(e => e.host.hasComponent(UprightComponent.id));
};
/**
 * Get evaluated snap action candidates for left & right uprights
 * @param mountingRail Mounting rail to attach to
 * @param uprightLeft Left upright
 * @param uprightRight Right upright
 * @param core Core
 * @param width Width of section
 * @returns An array of ValidSnapActionCandidateOrErrors
 */
const getUprightsSnapResults = (mountingRail, uprightLeft, uprightRight, core, width) => {
    const leftUprightSnapCandidate = getUprightSnapCandidate(uprightLeft, mountingRail, UPRIGHT_TO_MOUNTING_RAIL, UPRIGHT_TO_MOUNTING_RAIL_LEFT);
    const rightUprightSnapCandidate = getUprightSnapCandidate(uprightRight, mountingRail, UPRIGHT_TO_MOUNTING_RAIL, SIZE_TO_HOST_CONNECTOR_MAP[width]);
    const uprightsSnapResults = core
        .getServices()
        .validations.validateSnapCandidates([leftUprightSnapCandidate, rightUprightSnapCandidate]);
    return uprightsSnapResults;
};
const getDefaultPosition = (mountingRail, roomMinX) => {
    const floorOffset = mountingRail.getComponent(ComponentIds.RECOMMENDED_INITIAL_POSITION_FROM_FLOOR_COMPONENT_ID)?.offsetY;
    if (!floorOffset)
        throw new Error('Mounting rail is missing recommended initial position component');
    const width = mountingRail.getComponent(ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID)?.size.width;
    const wallOffset = (roomMinX ?? 0) + (width ?? 0);
    return new Vector(wallOffset, floorOffset, 0);
};
//# sourceMappingURL=uprightPlacement.js.map